import { usePermission, Tooltip } from 'portal-commons';
import React, { useState, FunctionComponent, MouseEvent } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuItem, Fade, Grid } from '@material-ui/core';
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';

import ConfirmationDialog from '../ConfirmationDialog';
import { MIGRATION_STATUS_LABEL_MAP } from '../../constants';
import { CampaignMigrationStatus } from '../../types';
import { remapTCRStatus } from '../../utils';
import { CmsRecord } from '../../../CSP-Form/types';
import { CampaignDetail } from '@/apis/core';
import { convertTimeWithTimezone } from '@/utils/time';
import { toastFlashMessage } from '@/utils';

interface CampaignDetailsBasicProps {
  campaign: CampaignDetail;
  renewalData?: CmsRecord;
  migrationInfo?: CampaignMigrationStatus;
  handleSubmitEdit?: (data: Partial<CampaignDetail>) => void;
}

const CampaignDetailsBasic: FunctionComponent<CampaignDetailsBasicProps> = ({
  campaign,
  renewalData,
  migrationInfo,
  handleSubmitEdit,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { hasPermission } = usePermission();

  const isReferenceIdTruncated = (campaign?.referenceId?.length ?? 0) > 6;

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
  };
  const handleRequestClose = () => {
    setOpen(false);
  };

  const getMigrationStatus = () => {
    const { status = 'No' } = migrationInfo ?? {};
    return (
      (MIGRATION_STATUS_LABEL_MAP as Record<string, string>)[status] ?? status
    );
  };

  return (
    <>
      <Grid
        container
        className="basic-info"
        spacing={2}
        data-testid="campaignDetailBasic"
      >
        <Grid item xs={4}>
          <div className="column">
            <h3 className="heading2" data-testid="campaignDetailBrandName">
              <span className="title">Brand DBA Name:</span>
              <span className="val">
                <a
                  data-testid="campaignDetailBasicBrandNameButton"
                  className="primary-color"
                  href={`/brands/${campaign.brandUid}`}
                >
                  {campaign.brandName}
                </a>
              </span>
            </h3>
            {campaign.subUsecases ? (
              <div className="campaign-usecase-wrapper">
                <h3
                  className="heading2"
                  aria-owns={open ? 'simple-menu' : undefined}
                  aria-haspopup="true"
                >
                  <span>Use-Case:</span>
                  <span
                    onClick={handleClick}
                    className="val"
                    data-testid="campaignDetailBasicUseCaseButton"
                  >
                    {campaign.usecase}
                    <ExpandMoreOutlinedIcon
                      className={`dropdown-arrow ${open ? 'open' : ''}`}
                    />
                  </span>
                </h3>
                <Menu
                  id="simple-menu"
                  data-testid="campaignDetailBasicSubUseCaseMenu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleRequestClose}
                  className="campaign-subusecases-menu"
                  TransitionComponent={Fade}
                >
                  {campaign.subUsecases.split(',').map((sub, index) => (
                    <MenuItem
                      key={index}
                      onClick={handleRequestClose}
                      data-testid={`campaignDetailBasicSubUseCase-${sub}`}
                    >
                      {sub}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            ) : (
              <h3
                className="heading2"
                data-testid="campaignDetailBasicSubUseCase"
              >
                <span>Use-Case:</span>
                {campaign.usecase}
              </h3>
            )}
            <h3 className="heading2" data-testid="campaignDetailBasicStatus">
              <span className="title">TCR Status:</span>
              <span
                className={`
                            val
                            ${
                              remapTCRStatus(campaign.status) === 'ACTIVE'
                                ? 'primary-color'
                                : remapTCRStatus(campaign.status) ===
                                  'DEACTIVATED'
                                ? 'error-color'
                                : ''
                            }
                            `}
              >
                {remapTCRStatus(campaign.status, true)}
              </span>
            </h3>
          </div>
        </Grid>

        <Grid item xs={4} className="border-left">
          <div className="column">
            <h3
              className="heading2"
              data-testid="campaignDetailBasicCreateDate"
            >
              <span>Registered on:</span>
              {campaign.createDate
                ? convertTimeWithTimezone(
                    campaign.createDate,
                    undefined,
                    'MM/DD/YYYY'
                  )
                : 'N/A'}
            </h3>
            <h3
              className="heading2"
              data-testid="campaignDetailBasicExpirationDate"
            >
              {remapTCRStatus(campaign.status) === 'DEACTIVATED' ? (
                <>
                  <span>Expiration Date:</span>
                  {campaign.expirationDate
                    ? convertTimeWithTimezone(
                        campaign.expirationDate,
                        undefined,
                        'MM/DD/YYYY'
                      )
                    : 'N/A'}
                </>
              ) : (
                <>
                  <span>
                    {campaign.autoRenewal ? 'Renewal' : 'Expiration'} Date:
                  </span>
                  {campaign.nextRenewalOrExpirationDate
                    ? convertTimeWithTimezone(
                        campaign.nextRenewalOrExpirationDate,
                        undefined,
                        'MM/DD/YYYY'
                      )
                    : 'N/A'}
                </>
              )}
            </h3>
            {remapTCRStatus(campaign.status) !== 'DEACTIVATED' && (
              <div className="auto-renewal-wrapper">
                <div
                  className={`${
                    !hasPermission('campaignDetail.autoRenewalToggle')
                      ? 'disabled'
                      : ''
                  } ${
                    campaign.autoRenewal
                      ? 'auto-renewal-button yes'
                      : 'auto-renewal-button no'
                  } ${campaign.status == 'EXPIRED' ? 'disabled' : ''}`}
                  onClick={() =>
                    campaign.autoRenewal
                      ? setOpenModal(true)
                      : handleSubmitEdit?.({ autoRenewal: true })
                  }
                  data-testid="campaignDetailBasicRenewalButton"
                >
                  {campaign.autoRenewal ? (
                    <>
                      Yes <span></span>
                    </>
                  ) : (
                    <>
                      <span></span>No
                    </>
                  )}
                </div>
                <p className="paragraph">Auto Renewal</p>
                <Tooltip
                  title="If set to “No” , the campaign will be deactivated at the end of the active period."
                  placement="right-end"
                  style={{
                    bottom: '2px',
                    right: '-8px',
                    top: undefined,
                    position: 'relative',
                    display: 'block',
                  }}
                />
              </div>
            )}
          </div>
        </Grid>
        <Grid item xs={4} className="border-left">
          <div className="column">
            <h3 className="heading2" data-testid="campaignDetailBasicMigration">
              <span className="title">Migration:</span>
              <span className="val primary-color">{getMigrationStatus()}</span>
            </h3>
            <h3
              className="heading2"
              data-testid="campaignDetailBasicDcaCompletion"
            >
              <span className="title">DCA Elections Complete:</span>
              <span className="val primary-color">
                {campaign.dcaCompletion ? 'Yes' : 'No'}
              </span>
            </h3>
            <h3
              className="heading2 reference-id"
              data-testid="campaignDetailBasicReferenceId"
            >
              <span>Reference ID:</span>
              <span className="val reference-id-wrapper">
                {isReferenceIdTruncated ? (
                  <Tooltip
                    className="text-tooltip"
                    style={{
                      color: '#333E41',
                      fontSize: '16px',
                      fontWeight: 600,
                    }}
                    arrow
                    placement="left"
                    title={campaign.referenceId!}
                  >
                    <div className="reference-id-text">
                      {isReferenceIdTruncated
                        ? `${campaign.referenceId?.trim().slice(0, 5)}...`
                        : campaign.referenceId?.trim() || 'N/A'}
                    </div>
                  </Tooltip>
                ) : (
                  <div className="reference-id-text">
                    {campaign.referenceId?.trim() || 'N/A'}
                  </div>
                )}
                {isReferenceIdTruncated && (
                  <CopyToClipboard
                    text={campaign.referenceId!}
                    onCopy={() =>
                      toastFlashMessage(
                        'Reference ID has been copied to your clipboard',
                        'success'
                      )
                    }
                  >
                    <Tooltip
                      title="copy"
                      placement="top"
                      arrow
                      style={{
                        padding: '5px',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCopy}
                        color="#0091B3"
                        fontSize={16}
                      />
                    </Tooltip>
                  </CopyToClipboard>
                )}
              </span>
            </h3>
          </div>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={openModal}
        type="renewal"
        handleCancel={() => setOpenModal(false)}
        handleSuccess={() => {
          setOpenModal(false);
          handleSubmitEdit?.({ autoRenewal: false });
        }}
        text={renewalData ? renewalData.text : ''}
      />
    </>
  );
};

export default CampaignDetailsBasic;
